// components/ui/Navlinks.tsx
'use client';

import { useState } from 'react';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import Logo from '@/components/icons/Logo';
import { ModeToggle } from '@/components/ui/Themes/ModeToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

import Button from '../Button/Button';
import s from './Navbar.module.css';
import { Menu } from 'lucide-react';
import {
  ChevronRight,
  Info,
  LayoutDashboard,
  Library,
  LifeBuoy,
  LogOut,
  Send,
  Settings2,
  TrendingUp,
  Users,
  Trophy
} from 'lucide-react';
import { signOut } from 'next-auth/react';

interface NavlinksProps {
  user?: any;
}

export default function Navlinks({ user }: NavlinksProps) {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  const navItems = [
    { href: '/articles', label: 'Articles' },
    { href: '/benchmarks', label: 'Benchmarks' },
    { href: '/partnerships', label: 'Partnerships' },
    { href: '/about_us', label: 'About Us' },
    { href: '/contact', label: 'Contact' }
  ];

  return (
    <div className='flex w-full items-center justify-between'>
      <Link
        href='/'
        className={`${s.logo} shrink-0`}
        aria-label='Logo'>
        <div className='flex items-center'>
          <Logo />
          <span
            className='text-brand-dark dark:text-brand-dark-text-primary ml-2 hidden text-lg font-bold sm:block sm:text-xl lg:text-2xl font-serif'
            style={{ fontFamily: "'Lora', serif" }}>
            Bioinfometrics
          </span>
        </div>
      </Link>

      <div className='flex items-center space-x-2 sm:space-x-3 lg:space-x-6'>
        <nav className='hidden items-center space-x-1 sm:flex lg:space-x-2'>
          {navItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={`${s.link} px-4 py-2 text-sm font-semibold transition-all lg:text-base ${
                pathname === item.href
                  ? 'border-brand-dark text-brand-dark dark:border-brand-dark-text-primary dark:text-brand-dark-text-primary border-b-2'
                  : 'text-muted-foreground hover:text-foreground'
              }`}>
              {item.label}
            </Link>
          ))}
          {user && (
            <Link
              href='/account'
              className={`${s.link} px-4 py-2 text-sm font-semibold transition-all lg:text-base ${
                pathname === '/account'
                  ? 'border-brand-dark text-brand-dark dark:border-brand-dark-text-primary dark:text-brand-dark-text-primary border-b-2'
                  : 'text-muted-foreground hover:text-foreground'
              }`}>
              Account
            </Link>
          )}
        </nav>

        <div className='hidden sm:block'>
          {!user && (
            <Link href='/auth/signin'>
              <Button
                variant='navbar'
                className='!px-6 !py-2 !text-xs lg:!text-sm'>
                SUBSCRIBE
              </Button>
            </Link>
          )}
        </div>

        <div className='hidden items-center space-x-2 sm:flex'>
          {user && <UserMenu user={user} />}
          <ModeToggle />
        </div>

        <Sheet
          open={isOpen}
          onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <button type="button"
              className='group bg-muted/30 hover:bg-muted/50 flex size-10 items-center justify-center rounded-xl transition-all hover:scale-105 focus:outline-none active:scale-95 sm:hidden'
              onClick={() => setIsOpen(true)}>
              <Menu className='size-5' />
              <span className='sr-only'>Toggle menu</span>
            </button>
          </SheetTrigger>
          <SheetContent
            side='right'
            className='bg-card/98 flex w-full flex-col border-none p-0 shadow-2xl backdrop-blur-2xl sm:w-[380px]'>
            {/* Header Area */}
            <div className='flex items-center justify-between px-8 pt-10 pb-6'>
              <Link
                href='/'
                className='flex items-center space-x-3'
                onClick={() => setIsOpen(false)}>
                <div className='bg-brand-dark flex size-9 items-center justify-center rounded-xl p-1.5 shadow-lg'>
                  <Logo className='h-full w-full' />
                </div>
                <span
                  className='text-foreground text-xl font-bold tracking-tight font-serif'
                  style={{ fontFamily: "'Lora', serif" }}>
                  Bioinfometrics
                </span>
              </Link>
              <div className='flex items-center space-x-2'>
                <ModeToggle />
              </div>
            </div>

            {/* Navigation Content */}
            <div className='flex flex-1 flex-col px-6'>
              {/* User Context */}
              {user && (
                <div className='mb-8 px-2'>
                  <div className='bg-primary/5 ring-primary/10 flex items-center space-x-4 rounded-3xl p-4 ring-1'>
                    <div className='bg-brand-dark flex size-12 shrink-0 items-center justify-center rounded-2xl text-lg font-bold text-white shadow-lg'>
                      {user?.name?.charAt(0).toUpperCase() ||
                        user?.email?.charAt(0).toUpperCase() ||
                        'R'}
                    </div>
                    <div className='flex flex-1 flex-col truncate'>
                      <span className='text-foreground truncate text-sm font-bold'>
                        {user?.name || user?.email?.split('@')[0]}
                      </span>
                      <span className='text-muted-foreground truncate text-[11px] font-medium'>
                        Premium Member
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Main Links */}
              <nav className='flex flex-col space-y-1'>
                {navItems.map((item) => {
                  const isActive = pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={`group flex items-center space-x-4 rounded-2xl px-4 py-4 transition-all active:scale-[0.98] ${
                        isActive
                          ? 'bg-muted/50 text-foreground'
                          : 'text-muted-foreground hover:bg-muted/30 hover:text-foreground'
                      }`}
                      onClick={() => setIsOpen(false)}>
                      <div
                        className={`transition-all duration-300 ${isActive ? 'text-primary scale-110' : 'group-hover:scale-110'}`}>
                        {item.label === 'Articles' && (
                          <Library className='size-5' />
                        )}
                        {item.label === 'Benchmarks' && (
                          <Trophy className='size-5' />
                        )}
                        {item.label === 'Partnerships' && (
                          <Users className='size-5' />
                        )}
                        {item.label === 'About Us' && (
                          <Info className='size-5' />
                        )}
                        {item.label === 'Contact' && (
                          <Send className='size-5' />
                        )}
                      </div>
                      <span
                        className={`text-[17px] font-semibold tracking-tight transition-transform ${isActive ? 'translate-x-1' : ''}`}>
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Bottom Actions */}
            <div className='mt-auto p-8'>
              {user ? (
                <button type="button"
                  onClick={async () => {
                    setIsOpen(false);
                    await signOut({ callbackUrl: '/' });
                  }}
                  className='text-muted-foreground hover:text-destructive bg-muted/30 hover:bg-destructive/10 flex w-full items-center justify-center space-x-2 rounded-2xl py-4 text-sm font-bold transition-all active:scale-95'>
                  <LogOut className='size-4' />
                  <span>Sign Out</span>
                </button>
              ) : (
                <Link
                  href='/auth/signin'
                  onClick={() => setIsOpen(false)}
                  className='block w-full'>
                  <Button
                    variant='navbar'
                    className='!h-16 !w-full !rounded-[20px] text-base font-bold shadow-2xl ring-offset-4 hover:ring-2'>
                    Get Unlimited Access
                  </Button>
                </Link>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}

function UserMenu({ user }: { user?: any }) {
  const router = useRouter();

  const displayName = user?.name || user?.email?.split('@')[0] || 'Account';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className='group bg-muted/30 text-foreground hover:bg-muted/50 hover:ring-brand-dark/20 dark:hover:ring-brand-dark-text-primary/20 focus:ring-brand-dark/30 flex items-center space-x-2 rounded-full p-1.5 pr-3 transition-all hover:ring-2 focus:ring-2 focus:outline-none'>
          <div className='bg-brand-dark flex size-7 items-center justify-center rounded-full text-[10px] font-black text-white shadow-inner'>
            {displayName.charAt(0).toUpperCase()}
          </div>
          <span className='hidden text-xs font-bold tracking-tight sm:inline text-brand-dark dark:text-brand-dark-text-primary'>
            {displayName === 'Account' ? 'Join Hub' : displayName}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        className='border-border/20 bg-card/90 animate-in fade-in zoom-in-95 mt-3 w-64 overflow-hidden rounded-[1.25rem] border p-2 shadow-2xl backdrop-blur-xl duration-200'>
        <div className='px-3 py-3'>
          <p className='text-foreground text-[13px] font-black tracking-tight'>
            {displayName}
          </p>
          <p className='text-muted-foreground truncate text-[10px] font-bold opacity-60'>
            {user?.email}
          </p>
        </div>

        <DropdownMenuSeparator className='bg-border/10 mx-2' />

        <div className='space-y-0.5 p-1'>
          <Link href='/strategic-lab/partners'>
            <DropdownMenuItem className='group/item focus:bg-brand-dark/10 focus:text-brand-dark dark:focus:bg-brand-dark-text-primary/10 dark:focus:text-brand-dark-text-primary flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-xs font-bold tracking-tight transition-all'>
              <div className='bg-muted/50 group-focus/item:bg-brand-dark/20 dark:group-focus/item:bg-brand-dark-text-primary/20 flex size-7 items-center justify-center rounded-lg transition-colors'>
                <TrendingUp className='size-4' />
              </div>
              Researcher Radar
              <ChevronRight className='ml-auto size-3 opacity-0 transition-opacity group-focus/item:opacity-100' />
            </DropdownMenuItem>
          </Link>

          <Link href='/account'>
            <DropdownMenuItem className='group/item focus:bg-brand-dark/10 focus:text-brand-dark dark:focus:bg-brand-dark-text-primary/10 dark:focus:text-brand-dark-text-primary flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-xs font-bold tracking-tight transition-all'>
              <div className='bg-muted/50 group-focus/item:bg-brand-dark/20 dark:group-focus/item:bg-brand-dark-text-primary/20 flex size-7 items-center justify-center rounded-lg transition-colors'>
                <LayoutDashboard className='size-4' />
              </div>
              Command Center
              <ChevronRight className='ml-auto size-3 opacity-0 transition-opacity group-focus/item:opacity-100' />
            </DropdownMenuItem>
          </Link>

          <Link href='/account/settings'>
            <DropdownMenuItem className='group/item focus:bg-brand-dark/10 focus:text-brand-dark dark:focus:bg-brand-dark-text-primary/10 dark:focus:text-brand-dark-text-primary flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-xs font-bold tracking-tight transition-all'>
              <div className='bg-muted/50 group-focus/item:bg-brand-dark/20 dark:group-focus/item:bg-brand-dark-text-primary/20 flex size-7 items-center justify-center rounded-lg transition-colors'>
                <Settings2 className='size-4' />
              </div>
              Platform Settings
              <ChevronRight className='ml-auto size-3 opacity-0 transition-opacity group-focus/item:opacity-100' />
            </DropdownMenuItem>
          </Link>

          <DropdownMenuItem className='group/item focus:bg-brand-dark/10 focus:text-brand-dark dark:focus:bg-brand-dark-text-primary/10 dark:focus:text-brand-dark-text-primary flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-xs font-bold tracking-tight transition-all'>
            <div className='bg-muted/50 group-focus/item:bg-brand-dark/20 dark:group-focus/item:bg-brand-dark-text-primary/20 flex size-7 items-center justify-center rounded-lg transition-colors'>
              <LifeBuoy className='size-4' />
            </div>
            Support
          </DropdownMenuItem>
        </div>

        <DropdownMenuSeparator className='bg-border/20 mx-2' />

        <div className='p-1'>
          <DropdownMenuItem
            className='group/logout text-destructive focus:bg-destructive/10 focus:text-destructive flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-[10px] font-black tracking-wider uppercase transition-all'
            onSelect={(e) => {
              e.preventDefault();
              signOut({ callbackUrl: '/' });
            }}>
            <div className='bg-destructive/5 group-focus/logout:bg-destructive/10 flex size-7 items-center justify-center rounded-lg transition-colors'>
              <LogOut className='size-4 transition-transform group-hover/logout:translate-x-1' />
            </div>
            Log Out
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
